import React, { useEffect, useMemo, useState } from 'react';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { Button, Table } from 'react-bootstrap';
import { BrandAccessRightsService } from '../service';
import { FaCheck } from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import { ErrorToast } from '../../../utils/toasters';
import BrandAccessRightsModal from './BrandAccessRightsModal';
import BrandAccessRightsFilter from './BrandAccessRightsFilter';
import { debounce } from 'lodash';
import { BrandAclResponse } from '../types';

function BrandAccessRightsList() {
    const [updateList, setUpdateList] = useState(false);
    const [showAclModal, setShowAclModal] = useState(false);
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [users, setUsers] = useState<BrandAclResponse[]>([]);
    const [selectedUserData, setSelectedUserData] = useState<BrandAclResponse | undefined>(undefined);

    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        totalPages: 0,
        search: '',
    });

    useEffect(() => {
        BrandAccessRightsService.getBrandAclList(pagination)
            .then(data => {
                setUsers(data.data);
                setPagination(prev => ({ ...prev, totalPages: Math.ceil(data.count / pagination.perPage) }));
                setDataLoading(false);
            })
            .catch(err => ErrorToast(err));
    }, [pagination.page, updateList, pagination.search]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    const handleChange = useMemo(
        () =>
            debounce((evt: React.ChangeEvent<HTMLInputElement>) => {
                const { name, value } = evt.target;
                setPagination(prev => ({
                    ...prev,
                    [name]: value,
                }));
            }, 300),
        []
    );
    const openCreateModal = () => {
        setSelectedUserData(undefined);
        setShowAclModal(true);
    };

    const openEditModal = (selectedUser: BrandAclResponse) => {
        setSelectedUserData(selectedUser);
        setShowAclModal(true);
    };

    const closeModal = () => {
        setShowAclModal(false);
        setSelectedUserData(undefined);
        setUpdateList(prevState => !prevState);
    };

    return (
        <>
            <BrandAccessRightsModal show={showAclModal} closeModal={closeModal} selectedBrand={selectedUserData} />
            <Button variant="outline-primary" onClick={openCreateModal}>
                + Add position
            </Button>
            <BrandAccessRightsFilter handleChanges={handleChange} />
            <TableLayout
                pagination={pagination}
                listLength={users.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'brandAccessRightsList'}
            >
                <Table responsive className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                    <thead>
                        <tr className="even">
                            <th>ID</th>
                            <th>Key</th>
                            <th>Name EN</th>
                            <th>Name DE</th>
                            <th>Enabled</th>
                            <th>Default</th>
                            <th className="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users?.map(user => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.key ?? '-'}</td>
                                <td>{user.name ?? '-'}</td>
                                <td>{user.nameDe}</td>
                                <td>
                                    {user.isEnabled ? (
                                        <span className="word-break text-primary">
                                            <FaCheck />
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaX />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {user.isDefault ? (
                                        <span className="word-break text-primary">
                                            <FaCheck />
                                        </span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaX />
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <Button size="sm" onClick={() => openEditModal(user)}>
                                        Edit
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {users.length < 15 && !!users.length && <div className="h-180px"></div>}
                </Table>
            </TableLayout>
        </>
    );
}

export default BrandAccessRightsList;
